import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import {
    getManagedWorkQueues,
    removeManagedWorkQueueJobs,
    reorderManagedWorkQueue
} from '../../../api';
import {
    ManagedWorkQueueItem,
    ManagedWorkQueueFieldChange,
    ManagedWorkQueueSnapshot,
    ManagedWorkQueueType
} from '../../../models/managedWorkQueues/ManagedWorkQueue';

interface ManagedWorkQueuePanelProps {
    organizationId: string;
    organizationName?: string;
    organizations?: Array<{ id: string; name: string }>;
}

type ScopeFilter = 'current' | 'history' | 'all';
type QueueTab = ManagedWorkQueueType | 'All';

const queueLabels: Record<ManagedWorkQueueType, string> = {
    ModuleSync: 'Sync',
    Backup: 'Backup',
    Publish: 'Publish'
};
const queueTabs: Array<{ type: QueueTab; label: string }> = [
    { type: 'All', label: 'All' },
    { type: 'ModuleSync', label: 'Sync' },
    { type: 'Backup', label: 'Backup' },
    { type: 'Publish', label: 'Publish' }
];

const terminalStates = new Set(['Complete', 'Failed']);

const formatFieldChange = (change: ManagedWorkQueueFieldChange): string => {
    if (change.valuesRedacted) return `${change.fieldName}: value changed (hidden)`;
    if (change.changeType === 'Added') return `${change.fieldName}: added “${change.newValue || ''}”`;
    if (change.changeType === 'Removed') return `${change.fieldName}: removed (was “${change.oldValue || ''}”)`;
    return `${change.fieldName}: “${change.oldValue || ''}” → “${change.newValue || ''}”`;
};

const ManagedWorkQueuePanel: React.FC<ManagedWorkQueuePanelProps> = ({ organizationId, organizationName, organizations = [] }) => {
    const [selectedQueue, setSelectedQueue] = useState<QueueTab>('ModuleSync');
    const [snapshot, setSnapshot] = useState<ManagedWorkQueueSnapshot | null>(null);
    const [scope, setScope] = useState<ScopeFilter>('current');
    const [search, setSearch] = useState('');
    const [activeOrganizationId, setActiveOrganizationId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        setActiveOrganizationId('');
        setSelectedJobIds(new Set());
    }, [organizationId]);

    useEffect(() => {
        const removableIds = new Set((snapshot?.jobs || [])
            .filter(job => job.state === 'Waiting' || job.state === 'Blocked')
            .map(job => job.id));
        setSelectedJobIds(current => new Set(Array.from(current).filter(id => removableIds.has(id))));
    }, [snapshot]);

    const organizationOptions = useMemo(() => {
        const options = new Map(organizations.map(organization => [organization.id, organization.name]));
        (snapshot?.jobs || []).forEach(job => {
            if (job.organizationId && job.organizationName) options.set(job.organizationId, job.organizationName);
        });
        if (organizationId && !options.has(organizationId)) options.set(organizationId, organizationName || organizationId);
        return Array.from(options.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((left, right) => left.name.localeCompare(right.name));
    }, [organizationId, organizationName, organizations, snapshot]);

    const refresh = useCallback(async () => {
        setIsLoading(true);
        const result = await getManagedWorkQueues(activeOrganizationId || undefined, scope !== 'current');
        if (result.resultType === 'Ok') {
            setSnapshot(result.data);
            setError(null);
        } else {
            setError(result.errors?.join(', ') || 'Unable to load managed work queues.');
        }
        setIsLoading(false);
    }, [activeOrganizationId, scope]);

    useEffect(() => {
        refresh();
        if (scope !== 'current') return;
        const interval = window.setInterval(refresh, 5000);
        return () => window.clearInterval(interval);
    }, [refresh, scope]);

    const jobs = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        return (snapshot?.jobs || [])
            .filter(job => selectedQueue === 'All' || job.queueType === selectedQueue)
            .filter(job => scope === 'all'
                || (scope === 'history' ? terminalStates.has(job.state) : !terminalStates.has(job.state)))
            .filter(job => !normalizedSearch
                || job.applicationName?.toLowerCase().includes(normalizedSearch)
                || job.organizationName?.toLowerCase().includes(normalizedSearch)
                || job.requestedByName?.toLowerCase().includes(normalizedSearch))
            .sort((left, right) => {
                if (selectedQueue !== 'All' && left.position && right.position) return left.position - right.position;
                return new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime();
            });
    }, [snapshot, selectedQueue, scope, search]);

    const summaries = selectedQueue === 'All'
        ? snapshot?.queues || []
        : (snapshot?.queues || []).filter(queue => queue.queueType === selectedQueue);
    const activeCount = summaries.reduce((total, queue) => total + queue.activeCount, 0);
    const pendingCount = summaries.reduce((total, queue) => total + queue.pendingCount, 0);
    const canMutateQueue = Boolean(activeOrganizationId);
    const removableJobs = canMutateQueue
        ? jobs.filter(job => job.state === 'Waiting' || job.state === 'Blocked')
        : [];
    const allVisibleSelected = removableJobs.length > 0 && removableJobs.every(job => selectedJobIds.has(job.id));

    const toggleJob = (jobId: string, selected: boolean) => {
        if (!canMutateQueue) return;
        setSelectedJobIds(current => {
            const next = new Set(current);
            if (selected) next.add(jobId);
            else next.delete(jobId);
            return next;
        });
    };

    const toggleVisibleJobs = (selected: boolean) => {
        if (!canMutateQueue) return;
        setSelectedJobIds(current => {
            const next = new Set(current);
            removableJobs.forEach(job => selected ? next.add(job.id) : next.delete(job.id));
            return next;
        });
    };

    const removeSelected = async () => {
        if (!canMutateQueue || selectedJobIds.size === 0) return;
        const confirmed = window.confirm(
            `Remove ${selectedJobIds.size} selected queue item${selectedJobIds.size === 1 ? '' : 's'}? Waiting dependent work will also be removed.`
        );
        if (!confirmed) return;

        setIsRemoving(true);
        setError(null);
        try {
            const response = await removeManagedWorkQueueJobs({
                organizationId: activeOrganizationId,
                jobIds: Array.from(selectedJobIds)
            });
            if (response.resultType === 'Ok') {
                setSnapshot(response.data);
                setSelectedJobIds(new Set());
            } else {
                setError(response.errors?.join(', ') || 'Unable to remove the selected queue items.');
            }
        } finally {
            setIsRemoving(false);
            await refresh();
        }
    };

    const onDragEnd = async (result: DropResult) => {
        if (!canMutateQueue || selectedQueue === 'All') return;
        if (!result.destination || result.destination.index === result.source.index) return;
        const reorderable = jobs.filter(job => job.state === 'Waiting' || job.state === 'Blocked');
        const moved = jobs[result.source.index];
        const destinationJob = jobs[result.destination.index];
        if (!moved || !destinationJob || !reorderable.includes(moved) || !reorderable.includes(destinationJob)) return;
        const sourceIndex = reorderable.indexOf(moved);
        const destinationIndex = reorderable.indexOf(destinationJob);
        const reordered = [...reorderable];
        reordered.splice(sourceIndex, 1);
        reordered.splice(destinationIndex, 0, moved);
        setError(null);
        const response = await reorderManagedWorkQueue(selectedQueue, activeOrganizationId, reordered.map(job => job.id));
        if (response.resultType === 'Ok') setSnapshot(response.data);
        else setError(response.errors?.join(', ') || 'Unable to reorder the queue.');
        await refresh();
    };

    return (
        <Panel data-testid="managed-work-queue-panel">
            <Header>
                <div>
                    <Title>Queue Management</Title>
                    <Subtitle>Global concurrency is coordinated by the CMS worker. This page can be closed safely.</Subtitle>
                </div>
                <RefreshButton onClick={refresh} disabled={isLoading}>{isLoading ? 'Refreshing…' : 'Refresh'}</RefreshButton>
            </Header>

            <Toolbar>
                <Tabs>
                    {queueTabs.map(queue => (
                        <Tab key={queue.type} active={selectedQueue === queue.type} onClick={() => setSelectedQueue(queue.type)}>
                            {queue.label}
                        </Tab>
                    ))}
                </Tabs>
                <OrganizationFilter>
                    <label>Organization</label>
                    <select
                        aria-label="Queue organization"
                        value={activeOrganizationId}
                        onChange={event => {
                            setActiveOrganizationId(event.target.value);
                            setSelectedJobIds(new Set());
                        }}
                    >
                        <option value="">All organizations</option>
                        {organizationOptions.map(organization => (
                            <option key={organization.id} value={organization.id}>{organization.name}</option>
                        ))}
                    </select>
                </OrganizationFilter>
            </Toolbar>

            <Counts>
                {selectedQueue === 'All'
                    ? <span>Limits <strong>{summaries.map(queue => `${queueLabels[queue.queueType]} ${queue.limit}`).join(' · ') || '—'}</strong></span>
                    : <span>Limit <strong>{summaries[0]?.limit ?? 0}</strong></span>}
                <span>Active <strong>{activeCount}</strong></span>
                <span>Pending <strong>{pendingCount}</strong></span>
                {snapshot && !snapshot.enabled && <Disabled>Managed queues are disabled in this environment.</Disabled>}
            </Counts>

            <Filters>
                <select value={scope} onChange={event => setScope(event.target.value as ScopeFilter)}>
                    <option value="current">Current</option>
                    <option value="history">History</option>
                    <option value="all">Current + history</option>
                </select>
                <input aria-label="Search queue applications or organizations" placeholder="Search application, organization, or requester" value={search} onChange={event => setSearch(event.target.value)} />
            </Filters>

            <BulkActions>
                <SelectAll>
                    <input
                        aria-label="Select all visible queued items"
                        type="checkbox"
                        checked={allVisibleSelected}
                        disabled={removableJobs.length === 0}
                        onChange={event => toggleVisibleJobs(event.target.checked)}
                    />
                    Select visible queued items
                </SelectAll>
                {!canMutateQueue && <MutationHint>Select an organization to remove or reorder queued items.</MutationHint>}
                <SelectionControls>
                    <span>{selectedJobIds.size} selected</span>
                    {selectedJobIds.size > 0 && <ClearButton onClick={() => setSelectedJobIds(new Set())}>Clear</ClearButton>}
                    <RemoveButton onClick={removeSelected} disabled={!canMutateQueue || selectedJobIds.size === 0 || isRemoving}>
                        {isRemoving ? 'Removing…' : 'Remove selected'}
                    </RemoveButton>
                </SelectionControls>
            </BulkActions>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId={`managed-${selectedQueue}`} isDropDisabled={!canMutateQueue || selectedQueue === 'All'}>
                    {provided => (
                        <QueueList ref={provided.innerRef} {...provided.droppableProps}>
                            {jobs.map((job, index) => (
                                <QueueRow
                                    key={job.id}
                                    job={job}
                                    index={index}
                                    selected={selectedJobIds.has(job.id)}
                                    canReorder={canMutateQueue && selectedQueue !== 'All'}
                                    canSelect={canMutateQueue}
                                    onSelectedChange={selected => toggleJob(job.id, selected)}
                                />
                            ))}
                            {provided.placeholder}
                            {!isLoading && jobs.length === 0 && <Empty>No queue items match these filters.</Empty>}
                        </QueueList>
                    )}
                </Droppable>
            </DragDropContext>
        </Panel>
    );
};

const QueueRow: React.FC<{
    job: ManagedWorkQueueItem;
    index: number;
    selected: boolean;
    canReorder: boolean;
    canSelect: boolean;
    onSelectedChange: (selected: boolean) => void;
}> = ({ job, index, selected, canReorder, canSelect, onSelectedChange }) => {
    const reorderable = job.state === 'Waiting' || job.state === 'Blocked';
    const requester = job.requestedByName || 'Unknown user';
    const queuedAt = job.createdAt ? new Date(job.createdAt).toLocaleString() : 'an unknown date';
    return (
        <Draggable draggableId={job.id} index={index} isDragDisabled={!reorderable || !canReorder}>
            {provided => (
                <Row ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} immutable={!reorderable || !canReorder}>
                    <RowSelection>
                        <input
                            aria-label={`Select ${job.applicationName || 'queue'} ${queueLabels[job.queueType]} item`}
                            type="checkbox"
                            checked={selected}
                            disabled={!reorderable || !canSelect}
                            onClick={event => event.stopPropagation()}
                            onChange={event => onSelectedChange(event.target.checked)}
                        />
                    </RowSelection>
                    <Position>{job.position ? `#${job.position}` : '—'}</Position>
                    <Main>
                        <RowTitle>{job.applicationName || 'Unknown application'} <TypeBadge>{queueLabels[job.queueType]}</TypeBadge></RowTitle>
                        <Meta>{job.organizationName || 'Unknown organization'}</Meta>
                        {job.moduleName && <Meta>Module: {job.moduleName}</Meta>}
                        <Meta>
                            Queued by {requester} on {queuedAt}
                            {job.completedAt && <> · Completed {new Date(job.completedAt).toLocaleString()}</>}
                        </Meta>
                        {job.dependencies?.length > 0 && <Meta>Waiting for earlier queued work</Meta>}
                        {job.moduleFieldChanges?.length > 0 && (
                            <FieldChanges>
                                <summary>{job.moduleFieldChangeCount || job.moduleFieldChanges.length} changed field{(job.moduleFieldChangeCount || job.moduleFieldChanges.length) === 1 ? '' : 's'}</summary>
                                <FieldChangeList>
                                    {job.moduleFieldChanges.map((change, changeIndex) => (
                                        <li key={`${change.fieldName}-${changeIndex}`}>{formatFieldChange(change)}</li>
                                    ))}
                                    {job.moduleFieldChangeCount > job.moduleFieldChanges.length && (
                                        <li>{job.moduleFieldChangeCount - job.moduleFieldChanges.length} more changed fields</li>
                                    )}
                                </FieldChangeList>
                            </FieldChanges>
                        )}
                        {job.error && <JobError>{job.error}</JobError>}
                    </Main>
                    <Right>
                        <Status state={job.state}>{job.state}</Status>
                    </Right>
                </Row>
            )}
        </Draggable>
    );
};

const Panel = styled.section`border: 1px solid #d9e0ea; border-radius: 8px; background: #fff; padding: 20px; margin-bottom: 24px;`;
const Header = styled.div`display: flex; justify-content: space-between; gap: 16px; align-items: flex-start;`;
const Title = styled.h2`font-size: 20px; margin: 0 0 4px; color: #29384f;`;
const Subtitle = styled.p`margin: 0; color: #66758d; font-size: 13px;`;
const RefreshButton = styled.button`border: 1px solid #5284d8; color: #3568bd; background: #fff; border-radius: 4px; padding: 7px 12px; cursor: pointer;`;
const Toolbar = styled.div`display: flex; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-top: 18px;`;
const Tabs = styled.div`display: flex; gap: 4px;`;
const Tab = styled.button<{ active: boolean }>`border: 0; border-radius: 4px; padding: 8px 14px; cursor: pointer; background: ${props => props.active ? '#3568bd' : '#edf2f8'}; color: ${props => props.active ? '#fff' : '#34455f'};`;
const OrganizationFilter = styled.div`display: flex; gap: 6px; align-items: center; font-size: 12px; select { min-width: 220px; padding: 7px; border: 1px solid #cbd4e0; border-radius: 4px; background: #fff; }`;
const Counts = styled.div`display: flex; gap: 22px; align-items: center; margin: 16px 0; color: #53647d;`;
const Disabled = styled.span`color: #9a6712;`;
const Filters = styled.div`display: flex; gap: 8px; margin-bottom: 10px; select, input { padding: 8px; border: 1px solid #cbd4e0; border-radius: 4px; } input { flex: 1; }`;
const BulkActions = styled.div`display: flex; justify-content: space-between; align-items: center; gap: 12px; margin: 0 0 10px; min-height: 32px; color: #53647d; font-size: 12px;`;
const SelectAll = styled.label`display: flex; align-items: center; gap: 7px; cursor: pointer;`;
const MutationHint = styled.span`flex: 1; color: #7a5a18; text-align: center;`;
const SelectionControls = styled.div`display: flex; align-items: center; gap: 8px;`;
const ClearButton = styled.button`border: 0; color: #3568bd; background: transparent; cursor: pointer; padding: 6px;`;
const RemoveButton = styled.button`border: 1px solid #b33a3a; color: #a52626; background: #fff; border-radius: 4px; padding: 7px 11px; cursor: pointer; &:disabled { cursor: default; opacity: .45; }`;
const ErrorMessage = styled.div`background: #fff0f0; color: #a52626; padding: 10px; border-radius: 4px; margin-bottom: 10px;`;
const QueueList = styled.div`min-height: 48px;`;
const Row = styled.div<{ immutable: boolean }>`display: flex; gap: 12px; align-items: flex-start; border-top: 1px solid #e7ebf1; padding: 12px 6px; background: ${props => props.immutable ? '#fafbfd' : '#fff'}; cursor: ${props => props.immutable ? 'default' : 'grab'};`;
const RowSelection = styled.div`width: 18px; padding-top: 1px; input { margin: 0; }`;
const Position = styled.div`width: 42px; font-weight: 600; color: #53647d;`;
const Main = styled.div`flex: 1; min-width: 0;`;
const RowTitle = styled.div`font-weight: 600; color: #29384f;`;
const TypeBadge = styled.span`display: inline-block; margin-left: 5px; padding: 1px 5px; border-radius: 3px; background: #edf2f8; color: #53647d; font-size: 10px; font-weight: 600; vertical-align: 1px;`;
const Meta = styled.div`font-size: 11px; color: #718097; margin-top: 3px; word-break: break-word;`;
const FieldChanges = styled.details`font-size: 11px; color: #53647d; margin-top: 6px; summary { cursor: pointer; font-weight: 600; }`;
const FieldChangeList = styled.ul`margin: 5px 0 0 18px; padding: 0; li { margin-top: 3px; word-break: break-word; }`;
const JobError = styled.div`font-size: 12px; color: #a52626; margin-top: 5px;`;
const Right = styled.div`text-align: right; min-width: 120px;`;
const Status = styled.span<{ state: string }>`display: inline-block; font-size: 12px; font-weight: 600; color: ${props => props.state === 'Failed' ? '#a52626' : props.state === 'Complete' ? '#277744' : '#3568bd'};`;
const Empty = styled.div`padding: 18px; text-align: center; color: #718097;`;

export default ManagedWorkQueuePanel;
